/**
 * Pure helpers for the group targeting list. Targeting is saved as one document,
 * so bulk actions rewrite the list and save it once rather than calling an
 * endpoint per target.
 */

/**
 * Keys a target for selection. A target the server has not yet given an `_id`
 * falls back to its priority, which is stable while selecting since selection
 * and reordering are never active together.
 *
 * @param {Object} target
 * @returns {string}
 */
export const getTargetKey = ({ _id, priority }) => String(_id ?? `priority-${priority}`);

/**
 * Returns a copy of `targets` with `status` applied to those whose key is in `keys`.
 *
 * @param {Object[]} targets
 * @param {Set<string>} keys
 * @param {boolean} status
 * @returns {Object[]}
 */
export const setTargetStatus = (targets, keys, status) =>
  targets.map((target) => (keys.has(getTargetKey(target)) ? { ...target, status } : target));

/**
 * Returns a copy of `targets` without those whose key is in `keys`, renumbered
 * from one so the saved order has no gaps.
 *
 * @param {Object[]} targets
 * @param {Set<string>} keys
 * @returns {Object[]}
 */
export const removeTargets = (targets, keys) =>
  targets
    .filter((target) => !keys.has(getTargetKey(target)))
    .map((target, index) => ({ ...target, priority: index + 1 }));

/**
 * Maps page-shaped targets back to the `departments` array the save endpoint
 * expects. `status` is only sent when set, matching how the page always saved.
 *
 * @param {Object[]} targets
 * @returns {Object[]}
 */
export const buildDepartmentsPayload = (targets) =>
  targets.map(({ campaigns, groups, status, tags, target }) => {
    const department = {
      targettype: typeof target === 'string' ? target : target.value,
      sendergroups: (groups || []).map(({ value }) => ({ groupname: value })),
      ads: (campaigns || []).map(({ value }) => ({ text: value })),
      tags: (tags || []).map(({ value }) => ({ text: value }))
    };

    if (status) {
      department.status = status;
    }

    return department;
  });
